/* eslint-disable jsx-a11y/aria-role */
import useAuth from '@auth/hooks/use-auth.hook';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '~/components/common/header.component';
import Select from '~/components/common/select.component';
import SidebarNavigationItem from '~/components/common/sidebar-navigation-item.component';
import Sidebar from '~/components/common/sidebar.component';
import { ROUTES, ROLE_SLUGS } from '~/constants';
import useCompanyContext from '~/hooks/company-scope.hook';
import { hasRole } from '~/hooks/has-role.hook';
import useViewer from '~/hooks/viewer.hook';
import capitalize from '~/utils/string';
import ErrorContainer from './error.container';

interface PageProps {
  children: ReactNode;
}

interface MenuItem {
  link: string;
  icon: string;
  role: string | Array<string>;
  label: string;
  requiresCompanyContext: boolean;
  requiresPremium?: boolean;
}

const Page = ({ children }: PageProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { viewer } = useViewer();
  const {
    company,
    isPremium,
    companies,
    handleCompanyChange,
  } = useCompanyContext();
  const { handleLogout } = useAuth();

  const menuItems: Array<MenuItem> = [
    {
      link: ROUTES.AUDITS,
      icon: 'ClipboardListIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_USER,
        ROLE_SLUGS.COMPANY_READ_ONLY,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
        ROLE_SLUGS.READ_ONLY,
      ],
      label: capitalize(t('audits')),
      requiresCompanyContext: true,
    },
    {
      link: ROUTES.ACTIONS,
      icon: 'ViewGridIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_USER,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
        ROLE_SLUGS.COMPANY_READ_ONLY,
        ROLE_SLUGS.READ_ONLY,
      ],
      label: capitalize(t('acties')),
      requiresCompanyContext: true,
      requiresPremium: true,
    },
    {
      link: ROUTES.REMARKS,
      icon: 'AnnotationIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_USER,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
        ROLE_SLUGS.COMPANY_READ_ONLY,
        ROLE_SLUGS.READ_ONLY,
      ],
      label: capitalize(t('vaststellingen')),
      requiresCompanyContext: true,
    },
    {
      link: ROUTES.USERS,
      icon: 'UserIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
      ],
      label: capitalize(t('medewerkers')),
      requiresCompanyContext: true,
    },
    {
      link: ROUTES.SITES,
      icon: 'OfficeBuildingIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
      ],
      label: capitalize(t('sites')),
      requiresCompanyContext: true,
    },
    {
      link: ROUTES.COMPANIES,
      icon: 'BriefcaseIcon',
      role: [ROLE_SLUGS.ROOT, ROLE_SLUGS.TENANT_ADMINISTRATOR],
      label: capitalize(t('klanten')),
      requiresCompanyContext: false,
    },
    {
      link: ROUTES.TENANT_USERS,
      icon: 'UsersIcon',
      role: [ROLE_SLUGS.ROOT, ROLE_SLUGS.TENANT_ADMINISTRATOR],
      label: capitalize(
        t('{{tenant}} medewerkers', {
          replace: {
            tenant: viewer?.tenant?.name,
          },
        }),
      ),
      requiresCompanyContext: false,
    },
    {
      link: ROUTES.LAWS,
      icon: 'ScaleIcon',
      role: [ROLE_SLUGS.ROOT, ROLE_SLUGS.TENANT_ADMINISTRATOR],
      label: capitalize(t('wetgevingen')),
      requiresCompanyContext: false,
    },
    {
      link: ROUTES.CUSTOM_LAWS,
      icon: 'ScaleIcon',
      role: [
        ROLE_SLUGS.ROOT,
        ROLE_SLUGS.TENANT_ADMINISTRATOR,
        ROLE_SLUGS.TENANT_USER,
        ROLE_SLUGS.COMPANY_USER,
        ROLE_SLUGS.COMPANY_ADMINISTRATOR,
        ROLE_SLUGS.READ_ONLY,
      ],
      label: capitalize(t('eigen wetgevingen')),
      requiresCompanyContext: true,
    },
    {
      link: ROUTES.BASE_AUDITS,
      icon: 'TemplateIcon',
      role: [ROLE_SLUGS.ROOT, ROLE_SLUGS.TENANT_ADMINISTRATOR],
      label: capitalize(t('basis audits')),
      requiresCompanyContext: false,
    },
  ]
    .filter(({ role }) => hasRole({ viewer, requestedRole: role }))
    .filter(
      ({ requiresPremium }) =>
        !requiresPremium || (requiresPremium && isPremium),
    );

  const companyContextMenuItems = menuItems.filter(
    ({ requiresCompanyContext }) => requiresCompanyContext,
  );
  const globalMenuItems = menuItems.filter(
    ({ requiresCompanyContext }) => !requiresCompanyContext,
  );
  const showDivider =
    companyContextMenuItems.length > 0 && globalMenuItems.length > 0;

  return (
    <div className="flex min-h-screen w-full">
      <ErrorContainer />
      <Sidebar>
        {companyContextMenuItems.length > 0 && (
          <Select
            key="select"
            className="w-full mb-2"
            value={{ label: company?.name, value: company }}
            options={
              companies?.map((c) => ({ label: c?.name, value: c })) || []
            }
            onChange={(item) => handleCompanyChange(item.value)}
          />
        )}
        {companyContextMenuItems.map((menuItem) => (
          <SidebarNavigationItem
            key={menuItem.link}
            link={menuItem.link}
            isActive={router.pathname.startsWith(menuItem.link)}
            icon={menuItem.icon}
            role={menuItem.role}
          >
            {menuItem.label}
          </SidebarNavigationItem>
        ))}
        {showDivider && <div className="border-t border-main-800 my-4 " />}
        {globalMenuItems.map((menuItem) => (
          <SidebarNavigationItem
            key={menuItem.link}
            link={menuItem.link}
            isActive={router.pathname.startsWith(menuItem.link)}
            icon={menuItem.icon}
            role={menuItem.role}
          >
            {menuItem.label}
          </SidebarNavigationItem>
        ))}
      </Sidebar>
      <div className="relative flex flex-col w-0 flex-1 overflow-hidden">
        <Header onSignOut={handleLogout} />
        <main
          id="page-container-content"
          className="flex-1 relative overflow-y-auto focus:outline-none pt-16"
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default Page;
